import { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import Slider from 'react-slick';
import { arrowLeft, arrowRight } from '../../utils/constants';
import Modal from '../modal/Modal';
import Variables from '../../style/variables.scss';

interface SlideshowProps {
  name?: string;
  slideshowImages: Array<string>;
}

const Slideshow = (props: SlideshowProps) => {
  const sliderRef = useRef<Slider>(null);
  const sliderModalRef = useRef<Slider>(null);

  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [activeModalSlide, setActiveModalSlide] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const transitionSpeed: number = 500;

  const slickSettings = {
    draggable: false,
    swipe: false,
    arrows: false,
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    speed: transitionSpeed,
    beforeChange: (current: number, next: number) => {
      setActiveSlide(next);
    },
  };

  const modalSettings = {
    initialSlide: activeSlide,
    adaptiveHeight: true,
    dots: false,
    arrows: false,
    infinite: true,
    fade: true,
    speed: transitionSpeed,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current: number, next: number) => {
      setActiveModalSlide(next);
    },
  };

  const nextSlide = useCallback(() => sliderRef.current?.slickNext(), []);
  const prevSlide = useCallback(() => sliderRef.current?.slickPrev(), []);
  const nextModalSlide = useCallback(
    () => sliderModalRef.current?.slickNext(),
    []
  );
  const prevModalSlide = useCallback(
    () => sliderModalRef.current?.slickPrev(),
    []
  );

  useEffect(() => {
    if (openModal) {
      document.body.classList.add('lock_scroll');
    } else {
      document.body.classList.remove('lock_scroll');
    }

    return () => document.body.classList.remove('lock_scroll');
  }, [openModal]);

  useEffect(() => {
    if (openModal && sliderModalRef.current) {
      sliderModalRef.current.slickGoTo(activeSlide, true);
      setActiveModalSlide(activeSlide);
    }
  }, [openModal, activeSlide]);

  useEffect(() => {
    if (!openModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(false);
      if (e.key === 'ArrowLeft') prevModalSlide();
      if (e.key === 'ArrowRight') nextModalSlide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openModal, nextModalSlide, prevModalSlide]);

  return (
    <>
      <Modal
        id='slideshow'
        isOpen={openModal}
        onClose={setOpenModal}
        primaryColor={Variables.secondaryColor}
        secondaryColor={Variables.primaryColor}
      >
        <Slider ref={sliderModalRef} {...modalSettings}>
          {props.slideshowImages.map((x: string) => (
            <div className='slideshow_modal_container'>
              <img
                src={x}
                alt={`slideshow ${props.slideshowImages.indexOf(x)}`}
              />
            </div>
          ))}
        </Slider>
        <div className='slideshow_modal_buttons'>
          <div className='opacity_transition_low' onClick={prevModalSlide}>
            {arrowLeft(Variables.primaryColor)}
          </div>
          <div className='slideshow_modal_buttons_text'>
            {activeModalSlide + 1} / {props.slideshowImages.length}
          </div>
          <div className='opacity_transition_low' onClick={nextModalSlide}>
            {arrowRight(Variables.primaryColor)}
          </div>
        </div>
      </Modal>
      <div className={`slideshow ${!props.name ? '' : undefined}`}>
        <Slider ref={sliderRef} {...slickSettings}>
          {props.slideshowImages.map((x: string) => (
            <div
              className='slideshow_img pointer'
              onClick={() => setOpenModal(!openModal)}
            >
              <img
                src={x}
                alt={`slideshow_image_${props.slideshowImages.indexOf(x)}`}
                loading='lazy'
              />
            </div>
          ))}
        </Slider>
        <div className='slideshow_info'>
          <div className='slideshow_info_buttons'>
            <div className='opacity_transition_low' onClick={prevSlide}>
              {arrowLeft(Variables.primaryColor)}
            </div>
            <div className='slideshow_info_buttons_text'>
              {activeSlide + 1} / {props.slideshowImages.length}
            </div>
            <div className='opacity_transition_low' onClick={nextSlide}>
              {arrowRight(Variables.primaryColor)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Slideshow;
